import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { useAuthGuardStyles } from '../styles/AuthGuard.styles';
import { loginRequest } from '../services/msalConfig';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const styles = useAuthGuardStyles();
  const [authError, setAuthError] = useState(false);

  // Trigger login if not authenticated and not in progress
  useEffect(() => {
    console.log('AuthGuard: inProgress=', inProgress, 'isAuthenticated=', isAuthenticated, 'accounts=', accounts);
    // Wait for MSAL to finish initializing
    if (inProgress !== 'none') {
      return;
    }

    // If not authenticated, initiate login
    if (!isAuthenticated && accounts.length === 0) {
      instance
        .loginRedirect(loginRequest)
        .catch((error) => {
          console.error('AuthGuard: loginRedirect failed:', error);
          setAuthError(true);
        });
    }
  }, [inProgress, isAuthenticated, accounts, instance]);

  // Redirect to access-denied on auth failure
  useEffect(() => {
    if (authError) {
      navigate('/access-denied', { replace: true });
    }
  }, [authError, navigate]);

  // Show loading while MSAL is initializing
  if (inProgress !== 'none') {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner label="Authenticating..." />
      </div>
    );
  }

  // Show content if authenticated
  if (isAuthenticated && accounts.length > 0) {
    return <>{children}</>;
  }

  // This shouldn't happen, but fallback to loading
  return (
    <div className={styles.spinnerContainer}>
      <Spinner label="Loading..." />
    </div>
  );
};

export default AuthGuard;
